import React, { useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  getVarsoUrl,
  renderSingleWithLabel,
  renderMultipleWithLabel,
} from "../../../common/helpers/helpers";
import withErrorGuard from "../../../HOC/withErrorGuard";
import HelperText from "../../../components/atoms/HelperText";
import PublicationLinks from "../../../components/Publications/components/PublicationLinks";
import GeneWithRefGenomeLink from "../../../common/helpers/GeneWithRefGenomeLink";
import { getPathogenicityColorByAllNames } from "../../../common/helpers/Constant";
import {
  getGenomeName,
  getVariantResult,
  getVarsomeHost,
} from "../../../state/selectors/general.selectors";
import { getSchema } from "./transcripts/selectors/transcriptsSelectors.selectors";
import ConditionalLink from "../../../components/atoms/ConditionalLink/ConditonalLink";

const { shape, string, bool } = PropTypes;

const UniprotVariants = ({
  variantResult,
  schema,
  varsomeHost,
  genomeName,
}) => {
  const [showHelperText, setShowHelperText] = useState(false);

  const getTranscriptUrl = (transcript) => {
    let url;
    const parenthesisTextReg = /\((.*?)\)/g;
    const parenthesisText = transcript.match(parenthesisTextReg);
    let text;
    if (parenthesisText) {
      text = parenthesisText[0].replace("(", "").replace(")", "");
    }
    const transcriptReg = /^((NR_)|(NM_)|(ENST))\d+(\.\d+)?$/i;
    if (text && text.match(transcriptReg)) {
      url = `${varsomeHost}/transcript/${genomeName}/${text}`;
    } else {
      url = `${varsomeHost}/transcript/${genomeName}/${transcript}`;
    }
    return url;
  };

  let tmpl;
  if (schema && variantResult && variantResult.uniprot_variants) {
    const newSchema = schema.properties.uniprot_variants;
    const labels = newSchema.items.properties.items.items.properties;
    const values = variantResult.uniprot_variants;

    let modal;

    tmpl = values.map((value, i) => {
      return (
        <div className="varsome-container varsome-container__basic" key={i}>
          <div className="varsome-title varsome-clearfix">
            <a
              href={getVarsoUrl("https://www.uniprot.org/")}
              target="_blank"
              className="tw-font-semibold"
            >
              {newSchema.title}
            </a>
            <HelperText fieldDescriptionKey={newSchema.title} />
            <small>
              {"Version: "}
              <span>{value.version}</span>
            </small>
          </div>
          <div>
            {value.items.map((item, l) => {
              return (
                <div
                  className="varsome-inner-container"
                  key={`${i}-${l}-labels`}
                >
                  <div className="varsome-horiz-flex">
                    {item.clinicalsignificances &&
                    labels &&
                    labels.clinicalsignificances &&
                    labels.clinicalsignificances.label ? (
                      <div className="varsome-horiz-list-item">
                        <div className="varsome-horiz-list-label">
                          {labels.clinicalsignificances.label}
                        </div>
                        <div className="varsome-horiz-list-value">
                          {(item.clinicalsignificances || [])
                            .filter((x) => typeof x === "string")
                            .map((x) => x.toLowerCase())
                            .map((x) => {
                              const color = getPathogenicityColorByAllNames(x);
                              const fontWeight = color ? "bold" : "";
                              return (
                                <div
                                  style={{ color, fontWeight }}
                                  className="varsome-horiz-list-value"
                                  key={x}
                                >
                                  {x}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    ) : null}

                    {renderSingleWithLabel(
                      item,
                      "variant_type",
                      labels.variant_type.label
                    )}

                    {item.annotation_id && (
                      <div className="varsome-horiz-list-item">
                        <div className="varsome-horiz-list-label">
                          {labels.annotation_id.label}
                        </div>
                        <div className="varsome-horiz-list-value">
                          <a
                            href={getVarsoUrl(
                              `https://web.expasy.org/variant_pages/${item.annotation_id}.html`
                            )}
                            target="_blank"
                          >
                            {item.annotation_id}
                          </a>
                        </div>
                      </div>
                    )}
                    {(item.proteinname || item.protein_id) && (
                      <div className="varsome-horiz-list-item">
                        <div className="varsome-horiz-list-label">Protein</div>
                        <div className="varsome-horiz-list-value">
                          {item.proteinname && <div>{item.proteinname}</div>}
                          {item.protein_id && <div>{item.protein_id}</div>}
                        </div>
                      </div>
                    )}
                    {item.gene && (
                      <div className="varsome-horiz-list-item">
                        <div className="varsome-horiz-list-label">
                          {labels.gene.label}
                        </div>
                        <div className="varsome-horiz-list-value">
                          <GeneWithRefGenomeLink
                            href={`${varsomeHost}/gene/${item.gene}`}
                            target="_blank"
                          >
                            {item.gene}
                          </GeneWithRefGenomeLink>
                        </div>
                      </div>
                    )}
                    {item.transcripts && (
                      <div className="varsome-horiz-list-item">
                        <div className="varsome-horiz-list-label">
                          {labels.transcripts.label}
                        </div>
                        <div className="varsome-horiz-list-value">
                          {item.transcripts.map((t, k) => {
                            const transcriptUrl = getTranscriptUrl(t);
                            return (
                              <div key={`transcript-${i}-${k}`}>
                                <ConditionalLink
                                  href={transcriptUrl}
                                  text={t}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {renderSingleWithLabel(
                      item,
                      "somaticstatus",
                      labels.somaticstatus.label,
                      false
                    )}
                  </div>
                  {!Array.isArray(item.association) && (
                    <div className="varsome-horiz-flex">
                      {renderSingleWithLabel(
                        item,
                        "disease",
                        labels.disease.label,
                        false
                      )}
                      {item.disease_symbol && (
                        <div className="varsome-horiz-list-item">
                          <div className="varsome-horiz-list-label">
                            {labels.disease_symbol.label}
                          </div>
                          <div className="varsome-horiz-list-value">
                            <a
                              href={getVarsoUrl(
                                `https://ghr.nlm.nih.gov/search?query=${item.disease_symbol}`
                              )}
                              target="_blank"
                            >
                              {item.disease_symbol}
                            </a>
                          </div>
                        </div>
                      )}
                      {item.disease_alt_symbol && (
                        <div className="varsome-horiz-list-item">
                          <div className="varsome-horiz-list-label">
                            {labels.disease_alt_symbol.label}
                          </div>
                          <div className="varsome-horiz-list-value">
                            <a
                              href={getVarsoUrl(
                                `http://www.omim.org/entry/${
                                  item.disease_alt_symbol.split(":")[1]
                                }`
                              )}
                              target="_blank"
                            >
                              {item.disease_alt_symbol}
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {item.pub_med_references &&
                    item.pub_med_references.length > 0 && (
                      <div className="varsome-horiz-list-item">
                        <div className="varsome-horiz-list-label">
                          Publications:
                        </div>
                        <PublicationLinks ids={item.pub_med_references} />
                      </div>
                    )}

                  <div className="varsome-horiz-flex">
                    {renderMultipleWithLabel(
                      item,
                      "bed_comments",
                      labels.bed_comments.label,
                      false
                    )}
                  </div>
                  {Array.isArray(item.association) &&
                    item.association.length > 0 && (
                      <div>
                        <br />
                        <h5>Associated diseases</h5>
                        <table className="varsome-table">
                          <thead>
                            <tr>
                              <th>Disease</th>
                              <th>Disease symbol</th>
                              <th>Disease alt symbol</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.association.map((assoc, j) => (
                              <tr key={`${i}-${j}-association`}>
                                <td>
                                  <div>{assoc.disease}</div>
                                  {assoc.disease_description && (
                                    <a
                                      onClick={() =>
                                        setShowHelperText(!showHelperText)
                                      }
                                    >
                                      <HelperText
                                        customText={`
                                              <div className="tw-text-sm">
                                                          <h4>${assoc.disease}</h4>
                                                          <div style={{ marginTop: "1rem" }}>
                                                            ${assoc.disease_description}
                                                          </div>
                                              </div>
                                          `}
                                        title=""
                                        anchor={<span>[description]</span>}
                                      />
                                    </a>
                                  )}
                                </td>
                                <td>
                                  {assoc.disease_symbol ? (
                                    <span>{assoc.disease_symbol}</span>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>
                                  {assoc.disease_alt_symbol ? (
                                    <a
                                      href={getVarsoUrl(
                                        `http://www.omim.org/entry/${assoc.disease_alt_symbol}`
                                      )}
                                      target="_blank"
                                    >
                                      {assoc.disease_alt_symbol}
                                    </a>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  <br />
                  {item.evidences &&
                    Array.isArray(item.xrefs.cosmic_study) &&
                    item.xrefs.cosmic_study.length > 0 && (
                      <div>
                        <h5>Evidences</h5>
                        <div className="varsome-horiz-flex">
                          <div className="varsome-horiz-list-item">
                            <div className="varsome-horiz-list-label">
                              Cosmic study
                            </div>
                            <div className="varsome-horiz-list-value">
                              {item.evidences.cosmic_study}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  <br />
                </div>
              );
            })}
          </div>
          {modal}
        </div>
      );
    });
  }

  return <div className="varsome-result-fixed">{tmpl}</div>;
};

const mapStateToProps = (state) => ({
  variantResult: getVariantResult(state),
  schema: getSchema(state),
  varsomeHost: getVarsomeHost(state),
  genomeName: getGenomeName(state),
});

/* eslint-disable camelcase */
const met_criteriaShape = shape({
  label: string,
  read_only: bool,
  required: bool,
  type: string,
});

const user_explainShape = shape({
  items: shape({
    type: string,
  }),
  label: string,
  read_only: bool,
  required: bool,
  type: string,
});

const classificationsShape = shape({
  items: shape({
    properties: shape({
      met_criteria: met_criteriaShape,
      name: met_criteriaShape,
      strength: met_criteriaShape,
      user_explain: user_explainShape,
      user_explain_failed: user_explainShape,
    }),
    type: string,
  }),
  title: string,
  type: string,
});

const acmg_annotationShape = shape({
  properties: shape({}),
  title: string,
  type: string,
});

UniprotVariants.propTypes = {
  variantResult: shape({}),
  genomeName: string,
  schema: shape({
    description: string,
    properties: shape({
      acmg_annotation: acmg_annotationShape,
      additional_sources: classificationsShape,
      alt: met_criteriaShape,
      amp_annotation: acmg_annotationShape,
      bravo: classificationsShape,
      broad_exac: classificationsShape,
      cadd: classificationsShape,
      cancer_hotspots: classificationsShape,
      cbio_portal: classificationsShape,
      chromosome: met_criteriaShape,
      cytobands: met_criteriaShape,
      dann_snvs: classificationsShape,
      dbnsfp: classificationsShape,
      dbnsfp_dbscsnv: classificationsShape,
      dbnsfp_premium: classificationsShape,
      ensembl_transcripts: classificationsShape,
      equivalent: acmg_annotationShape,
      genomenon_mastermind: classificationsShape,
      gerp: classificationsShape,
      gnomad_exomes: classificationsShape,
      gnomad_exomes_coverage: classificationsShape,
      gnomad_genomes: classificationsShape,
      gnomad_genomes_coverage: classificationsShape,
      gnomad_mito: classificationsShape,
      gwas: classificationsShape,
      iarc_tp53_germline: classificationsShape,
      iarc_tp53_somatic: classificationsShape,
      icgc_somatic: classificationsShape,
      in_silico_thresholds: acmg_annotationShape,
      isb_kaviar3: classificationsShape,
      jax_ckb: classificationsShape,
      mitomap: classificationsShape,
      ncbi_clinvar2: classificationsShape,
      ncbi_dbsnp: classificationsShape,
      nih_gdc: classificationsShape,
      not_subscribed_sources: met_criteriaShape,
      original_variant: met_criteriaShape,
      partner_content: classificationsShape,
      pharmgkb: classificationsShape,
      phastcons100way: classificationsShape,
      phylop100way: classificationsShape,
      pos: met_criteriaShape,
      pub_med_articles: met_criteriaShape,
      pub_med_user_entries: classificationsShape,
      publications: met_criteriaShape,
      ref: met_criteriaShape,
      ref_seq: met_criteriaShape,
      refseq_transcripts: classificationsShape,
      regions: met_criteriaShape,
      sanger_cosmic: classificationsShape,
      sanger_cosmic_licensed: classificationsShape,
      sanger_cosmic_public: classificationsShape,
      saphetor_known_pathogenicity: classificationsShape,
      thousand_genomes: classificationsShape,
      uniprot_variants: classificationsShape,
      user_multiple_choice_questions: user_explainShape,
      variant_id: met_criteriaShape,
      variant_pubmed_automap: classificationsShape,
      variant_transcripts: user_explainShape,
      variant_type: met_criteriaShape,
      varsome_queries: acmg_annotationShape,
      weill_cornell_medicine_pmkb: classificationsShape,
      wustl_civic: classificationsShape,
      wustl_docm: classificationsShape,
    }),
    title: string,
    type: string,
  }),
  varsomeHost: string,
};

UniprotVariants.defaultProps = {
  variantResult: null,
  genomeName: null,
  schema: null,
  varsomeHost: null,
};

export default connect(mapStateToProps)(
  withErrorGuard(UniprotVariants, "Uniprot Variants", "UniProt variants")
);
